import BaseStore from '@/store/BaseStore'
import { Module, Mutation } from '@/node_modules/vuex-module-decorators'
import {
  BibliographyFullIdWithMarcNo,
  defaultBibliographyFullIdWithMarcNo
} from '@/types'
import { dataStore } from '@/utils/store-accessor'

export interface FirstBibliographyInfo extends BibliographyFullIdWithMarcNo {
  contentId?: number
}

export const defaultFirstBibliographyInfo = (): FirstBibliographyInfo => ({
  ...defaultBibliographyFullIdWithMarcNo(),
  contentId: undefined
})

export const FirstTransitionToBibliographyDetail = {
  Not: '', // 初期状態、照会を一度も表示していない事を示す
  Search: 'search', // 資料の検索からの遷移（実質一件検索で直接遷移する場合）
  SearchResult: 'searchResult', // 資料の検索結果（典拠、内容含む）からの遷移
  SimpleOrdering: 'simpleOrdering', // 簡易発注から遷移
  SearchCreate: 'searchCreate', // 検索画面の書誌新規登録からの遷移
  SearchResultCreate: 'searchResultCreate', // 検索結果画面の書誌新規登録からの遷移
  Pined: 'pined' // ピン留め履歴から遷移
}
export type FirstTransitionToBibliographyDetail = typeof FirstTransitionToBibliographyDetail[keyof typeof FirstTransitionToBibliographyDetail]

// 巻号周りの遷移については別のフラグを作成して分離してもいいかもしれない
export const SecondAfterTransitionToBibliographyDetail = {
  Not: '', // 最初の書誌からそれ以降遷移が行われていない事を示す、「最初の書誌へ」押下直後もこれに当たる
  Pagination: 'pagination', // ペジネーションによる遷移
  VolumePagination: 'volumePagination', // ペジネーションによる巻号書誌間遷移、検索結果内の遷移ではなく、対象の関連集合書誌の巻号書誌間遷移という意味
  Volume: 'volume', // 関連巻号書誌から遷移
  Set: 'set', // 関連集合書誌から遷移
  Copy: 'copy', // 複写登録から遷移
  ParentCollectionId: 'parentCollectionId', // 親資料番号からの遷移
  ChildCollectionList: 'childCollectionList' // 下位資料一覧から遷移
}
export type SecondAfterTransitionToBibliographyDetail = typeof SecondAfterTransitionToBibliographyDetail[keyof typeof SecondAfterTransitionToBibliographyDetail]

@Module({
  name: 'BibliographyDetailStore',
  namespaced: true,
  stateFactory: true
})

// 資料照会の物理・集合共用で遷移に関するフラグを管理しているstore
// 現状は他画面への遷移に関するボタンの表示制御を行っている（ペジネーション、下部ボタン）
// あくまで共通化できる条件式をまとめているだけなので、それぞれのコンポーネント側で表示条件を追加している場合もある
// 条件式については確認をしておくように
export default class BibliographyDetailStore extends BaseStore {
  // 最初に遷移した書誌情報、「最初の書誌へ」ボタンで使用する
  _firstBibliographyInfo: FirstBibliographyInfo = defaultFirstBibliographyInfo()

  // 最初に資料照会を開いた時、どこから照会に遷移したか判定する変数
  _firstTransitionSource: FirstTransitionToBibliographyDetail =
    FirstTransitionToBibliographyDetail.Not

  // 二回目以降、資料照会を開いた時、どこから照会に遷移したか判定する変数
  _secondAfterTransitionSource: SecondAfterTransitionToBibliographyDetail =
    SecondAfterTransitionToBibliographyDetail.Not

  // ピン留め履歴から遷移したか判定する変数、特殊なので別に分けることにする
  _isTransitionPined: boolean = false

  // ピン留め履歴から最初に遷移した書誌情報、型を統一しておきたいので内容IDを設定
  _firstBibliographyInfoByPined: FirstBibliographyInfo = defaultFirstBibliographyInfo()

  // ピン留め履歴の照会画面以降、どこから照会に遷移したか判定する変数
  _secondAfterTransitionSourceByPined: SecondAfterTransitionToBibliographyDetail =
    SecondAfterTransitionToBibliographyDetail.Not

  // 初期選択とする資料の資料番号
  _initialSelectCollectionNo: string = ''

  // 除籍表示スイッチの値
  _isDisplayRemoved: boolean = false

  get firstBibliographyInfo(): FirstBibliographyInfo {
    return this._isTransitionPined
      ? this._firstBibliographyInfoByPined
      : this._firstBibliographyInfo
  }

  get firstTransitionSource(): FirstTransitionToBibliographyDetail {
    return this._firstTransitionSource
  }

  get secondAfterTransitionSource(): SecondAfterTransitionToBibliographyDetail {
    return this._isTransitionPined
      ? this._secondAfterTransitionSourceByPined
      : this._secondAfterTransitionSource
  }

  // ペジネーション表示判定
  // 基本的にはリロード等で検索結果一覧が無い場合にtrueとする認識
  get showPagination(): boolean {
    return (
      (([
        FirstTransitionToBibliographyDetail.SearchResult,
        FirstTransitionToBibliographyDetail.Search
      ].includes(this.firstTransitionSource) &&
        !this._isTransitionPined) ||
        [
          SecondAfterTransitionToBibliographyDetail.VolumePagination,
          SecondAfterTransitionToBibliographyDetail.Set
        ].includes(this.secondAfterTransitionSource)) &&
      ![
        SecondAfterTransitionToBibliographyDetail.Copy,
        SecondAfterTransitionToBibliographyDetail.ParentCollectionId,
        SecondAfterTransitionToBibliographyDetail.ChildCollectionList
      ].includes(this.secondAfterTransitionSource)
    )
  }

  // 「検索結果へ」遷移非活性化判定
  get disabledTransitionSearchResult(): boolean {
    return (
      [
        FirstTransitionToBibliographyDetail.Not,
        FirstTransitionToBibliographyDetail.SimpleOrdering,
        FirstTransitionToBibliographyDetail.SearchCreate,
        FirstTransitionToBibliographyDetail.SearchResultCreate
      ].includes(this.firstTransitionSource) ||
      this._isTransitionPined ||
      this.secondAfterTransitionSource ===
        SecondAfterTransitionToBibliographyDetail.Copy
    )
  }

  // 「最初の書誌へ」遷移非活性化判定
  get disabledTransitionFirstBibliography(): boolean {
    return (
      (([
        FirstTransitionToBibliographyDetail.Not,
        FirstTransitionToBibliographyDetail.SimpleOrdering,
        FirstTransitionToBibliographyDetail.SearchCreate,
        FirstTransitionToBibliographyDetail.SearchResultCreate
      ].includes(this.firstTransitionSource) ||
        this._isTransitionPined) &&
        [
          SecondAfterTransitionToBibliographyDetail.Not,
          SecondAfterTransitionToBibliographyDetail.Copy
        ].includes(this.secondAfterTransitionSource)) ||
      [
        SecondAfterTransitionToBibliographyDetail.Not,
        SecondAfterTransitionToBibliographyDetail.Copy
      ].includes(this.secondAfterTransitionSource)
    )
  }

  get isTransitionPined(): boolean {
    return this._isTransitionPined
  }

  get initialSelectCollectionNo(): string {
    return this._initialSelectCollectionNo
  }

  get isDisplayRemoved(): boolean {
    return this._isDisplayRemoved
  }

  @Mutation
  public setFirstBibliographyInfo(value: FirstBibliographyInfo) {
    if (this._isTransitionPined) {
      this._firstBibliographyInfoByPined = value
    } else {
      this._firstBibliographyInfo = value
    }
  }

  @Mutation
  public setFirstTransitionSource(value: FirstTransitionToBibliographyDetail) {
    // フラグ変数を初期化する
    this._firstBibliographyInfo = defaultBibliographyFullIdWithMarcNo()
    this._secondAfterTransitionSource =
      SecondAfterTransitionToBibliographyDetail.Not
    this._isTransitionPined = false
    this._secondAfterTransitionSourceByPined =
      SecondAfterTransitionToBibliographyDetail.Not

    this._firstTransitionSource = value
  }

  @Mutation
  public setSecondAfterTransitionSource(
    value: SecondAfterTransitionToBibliographyDetail
  ) {
    if (this._isTransitionPined) {
      this._secondAfterTransitionSourceByPined = value
    } else {
      this._secondAfterTransitionSource = value
    }
  }

  @Mutation
  public setIsTransitionPined(value: boolean) {
    this._isTransitionPined = value
    this._firstBibliographyInfoByPined = defaultBibliographyFullIdWithMarcNo()
    this._secondAfterTransitionSourceByPined =
      SecondAfterTransitionToBibliographyDetail.Not
  }

  @Mutation
  public setInitialSelectCollectionNo(value: string) {
    this._initialSelectCollectionNo = value
  }

  @Mutation
  public setIsDisplayRemoved(value: boolean) {
    this._isDisplayRemoved = value
  }

  // 全削除
  @Mutation
  public reset() {
    this._firstBibliographyInfo = defaultBibliographyFullIdWithMarcNo()
    this._firstTransitionSource = FirstTransitionToBibliographyDetail.Not
    this._secondAfterTransitionSource =
      SecondAfterTransitionToBibliographyDetail.Not
    this._isTransitionPined = false
    this._firstBibliographyInfoByPined = defaultBibliographyFullIdWithMarcNo()
    this._secondAfterTransitionSourceByPined =
      SecondAfterTransitionToBibliographyDetail.Not
    this._initialSelectCollectionNo = ''
    this._isDisplayRemoved =
      dataStore.bibliographyDetailPageDisplayRemovedSwitchDefaultValue
  }
}
